import { createMachine } from '@/lib/stateMachine';

/**
 * Quality Event status definitions.
 * Each status has a display label and a color token (maps to shadcn badge variants).
 */
export const QUALITY_STATUSES = {
  DRAFT: { label: 'Draft', color: 'gray' },
  REVIEWING: { label: 'Reviewing', color: 'blue' },
  APPROVED: { label: 'Approved', color: 'teal' },
  REJECTED: { label: 'Rejected', color: 'red' },
  RETURNED: { label: 'Returned', color: 'yellow' },
  PENDING_ACTION: { label: 'Pending Action', color: 'amber' },
  PENDING_EC: { label: 'Pending EC', color: 'orange' },
  CLOSED: { label: 'Closed', color: 'slate' },
};

/**
 * State machine configuration for the Quality Event lifecycle.
 *
 * Approval pipeline: PO → QA → MGMT (same as documents).
 * Post-approval: QA releases to PENDING_ACTION when action plans are required,
 * or directly to CLOSED when no action plan is needed.
 * PENDING_ACTION → checks action plans (PENDING_EC) → checks effectiveness (CLOSED).
 * Terminal states: REJECTED, CLOSED.
 *
 * Review group order matches the approvals table arrays:
 *   0 — processOwners (PO)
 *   1 — qmrQa (QA)
 *   2 — managementApprovers (MGMT)
 */
export const qualityMachineConfig = {
  initial: 'DRAFT',
  reviewGroupOrder: ['processOwners', 'qmrQa', 'managementApprovers'],
  states: {
    DRAFT: {
      actions: {
        submit: { next: 'REVIEWING', actorRule: 'creator' },
        edit: { next: null, actorRule: 'creator' }, // handled inline via PUT
        delete: { next: null, actorRule: 'creator' }, // handled inline via DELETE (soft-delete)
      },
    },
    REVIEWING: {
      actions: {
        approve: { next: 'dynamic', actorRule: 'pipeline' },
        reject: { next: 'REJECTED', actorRule: 'pipeline', resetIndex: true },
        returnToRequestor: { next: 'RETURNED', actorRule: 'pipeline', resetIndex: true },
        linkDocument: { next: null, actorRule: 'role', requiredRole: ['QA'] },
      },
    },
    RETURNED: {
      actions: {
        edit: { next: null, actorRule: 'creator' }, // Q8: creator edits after return
        submit: { next: 'REVIEWING', actorRule: 'creator', resetIndex: true }, // Q8: restart at PO
      },
    },
    APPROVED: {
      actions: {
        release: { next: null, actorRule: 'role', requiredRole: ['QA'] },
      },
    },
    PENDING_ACTION: {
      actions: {
        checkActionPlans: { next: 'PENDING_EC', actorRule: 'role', requiredRole: ['QA'] },
      },
    },
    PENDING_EC: {
      actions: {
        checkEffectiveness: { next: 'CLOSED', actorRule: 'role', requiredRole: ['QA'] },
      },
    },
    CLOSED: {
      actions: {}, // terminal state
    },
    REJECTED: {
      actions: {}, // terminal state — only visible to QA
    },
  },
};

/**
 * Quality Event state machine instance.
 *
 * Usage — getActions():
 *   qualityMachine.getActions(status, userSub, reviewerPipeline, currentActorIndex, creatorSub, userRole)
 *
 * IMPORTANT: `userRole` must be passed as an **array** of role strings (e.g., `['QA']` or `['QA', 'Management']`).
 * The underlying `getActions()` implementation calls `userRole?.some(...)`, so passing a plain string will throw.
 * Calling components should derive this from the user's Cognito groups.
 */
export const qualityMachine = createMachine(qualityMachineConfig);
